<template>
  <Field :name="id" :rules="resolvedRules" v-slot="{ field, errorMessage, meta }">
    <div class="relative w-full">
      <!-- Left Icon Slot -->
      <span
        v-if="$slots['icon-left'] || leftIcon"
        class="absolute inset-y-0 left-3 flex items-center"
        :class="iconSize"
      >
        <slot name="icon-left">
          <component :is="leftIcon" class="text-gray-500" v-if="leftIcon"></component>
        </slot>
      </span>

      <!-- Label -->
      <label
        v-if="label"
        :for="id"
        class="transition-all duration-200 ease-in-out"
        :class="[
          floatingLabel ? 'absolute left-4' : 'relative mb-2 block',
          floatingLabel && (field.value || isFocused) ? labelSize.active : labelSize.inactive,
          !floatingLabel ? labelSize.static : '',
        ]"
      >
        {{ label }}
      </label>

      <!-- Input -->
      <input
        v-bind="field"
        :id="id"
        :type="type"
        :maxlength="maxlength"
        :placeholder="floatingLabel ? ' ' : placeholder"
        class="block w-full rounded-md border focus:outline-none"
        :class="[
          inputSize,
          inputPadding,
          borderClasses(errorMessage),
          inputTextColor,
          {
            'pt-4': floatingLabel && (field.value || isFocused),
            'pl-10': $slots['icon-left'] || leftIcon,
            'pr-10': $slots['icon-right'] || rightIcon,
          },
        ]"
        :disabled="disabled"
        @focus="isFocused = true"
        @blur="handleBlur"
      />

      <span v-if="errorMessage" class="block border border-[#D32F2F] px-4 py-3 bg-[#FBEBEB] rounded-[2px] text-[13px] mt-2">{{errorMessage}}</span>
      <!-- Right Icon Slot -->
      <span
        v-if="$slots['icon-right'] || rightIcon"
        class="absolute inset-y-0 right-3 flex items-center"
        :class="iconSize"
      >
        <slot name="icon-right">
          <component :is="rightIcon" class="text-gray-500" v-if="rightIcon"></component>
        </slot>
      </span>
    </div>
  </Field>
</template>

<script>
import { Field } from 'vee-validate';
import { required, email, min, max, numeric } from '@vee-validate/rules';
import { ref } from 'vue';

export default {
  name: 'InputField',
  components: { Field },
  props: {
    id: String,
    type: {
      type: String,
      default: 'text',
    },
    label: String,
    placeholder: String,
    disabled: Boolean,
    floatingLabel: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: Number,
      default: 240,
    },
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    leftIcon: [String, Object],
    rightIcon: [String, Object],
    ruleType: {
      type: [String, Array, Object],
      default: '',
    },
  },
  data() {
    return {
      isFocused: false,
    };
  },
  computed: {
    resolvedRules() {
      const type = this.ruleType;
      if (!type) return undefined;

      if (typeof type === 'string') {
        const rules = {};
        type.split('|').forEach(rule => {
          const [ruleName, arg] = rule.split(':');

          switch (ruleName) {
            case 'required':
              rules.required = true;
              break;
            case 'email':
              rules.email = true;
              break;
            case 'numeric':
              rules.numeric = true;
              break;
            case 'min':
              if (arg && !isNaN(arg)) {
                rules.min = Number(arg);
              }
              break;
            case 'max':
              if (arg && !isNaN(arg)) {
                rules.max = Number(arg);
              }
              break;
            default:
              break;
          }
        });
        return rules;
      }

      // If the type is an object, directly map to rules
      if (typeof type === 'object') {
        const rules = {};
        if (type.required) rules.required = true;
        if (type.email) rules.email = true;
        if (type.numeric) rules.numeric = true;
        if (type.min && typeof type.min === 'number') rules.min = type.min;
        if (type.max && typeof type.max === 'number') rules.max = type.max;
        return rules;
      }

      return undefined;
    },
    hasValue() {
      return this.$attrs.modelValue?.trim?.().length > 0;
    },
    borderClasses() {
      return (errorMessage) => {
        return {
          'border border-red-500 focus:border-red-500': errorMessage,
          'border-gray border-solid hover:bg-turquoise-10 hover:border-turquoise-40 focus:border-turquoise-60 focus:border-2':
            !errorMessage,
          'cursor-not-allowed opacity-50': this.disabled,
        };
      };
    },
    inputTextColor() {
      return 'text-[#000]';
    },
    inputSize() {
      return {
        sm: 'h-8 text-sm',
        md: 'h-12 text-base',
        lg: 'h-14 text-lg',
      }[this.size || 'lg'];
    },
    inputPadding() {
      return {
        sm: 'px-2',
        md: 'px-4',
        lg: 'px-4',
      }[this.size || 'lg'];
    },
    iconSize() {
      return {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
      }[this.size || 'lg'];
    },
    labelSize() {
      return {
        active: {
          sm: 'text-form-label text-[#5E5E5E] top-[10px]',
          md: 'text-form-label text-[#5E5E5E] top-[10px]',
          lg: 'text-form-label text-[#5E5E5E] top-[10px]',
        }[this.size || 'lg'],
        inactive: {
          sm: 'text-form-input text-[#5E5E5E] top-4',
          md: 'text-form-input text-[#5E5E5E] top-4',
          lg: 'text-form-input text-[#5E5E5E] top-4',
        }[this.size || 'lg'],
        static: {
          sm: 'text-form-label text-[#5E5E5E] top-0',
          md: 'text-form-label text-[#5E5E5E] top-0',
          lg: 'text-form-label text-[#5E5E5E] top-0',
        }[this.size || 'lg'],
      };
    },
  },
  methods: {
    handleBlur() {
      this.isFocused = false;
    },
  },
  watch: {
    input(val){
    }
  }
};
</script>
